import { useEffect, useRef, useState } from 'react';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import { ViewModelSettingModal, ViewModelDeleteConfirmModal } from '@view-model/ui/components/ViewModel';
import type { Workspace } from '@workspace/domain/workspace/Workspace';
import { ItemContent } from './ItemContent';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useViewModelCopy } from '../../hooks';
import { MoveToOtherWorkspaceModal } from '../MoveToOtherWorkspaceModal';
import { useCurrentUserId } from '@framework/auth';
import { MoveViewModelModal } from '../MoveModal/MoveViewModelModal';
import { ConfigureIndividualPermissions } from '../modals/individual-permissions/ConfigureIndividualPermissions';
import { usePrimitiveValueListener } from '@framework/repository';
import { RTDBPath } from 'balus-common';
import { useIsViewModelRestrictedByGroupLicensePermission } from '@group/License';

type Props = {
  showEditMenu: boolean;
  viewModel: ViewModelEntity;
  workspace: Workspace;
  showConfigureIndividualPermissions: boolean;
  noMenu?: boolean;
  shouldBeScrolled?: boolean;
};

type ModalTarget = 'Edit' | 'Delete' | 'MoveViewModel' | 'MoveToOtherWorkspace' | 'ConfigureIndividualPermissions';

export const ViewModelItem: React.FC<Props> = ({
  showEditMenu,
  viewModel,
  workspace,
  showConfigureIndividualPermissions,
  noMenu = false,
  shouldBeScrolled = false,
}: Props) => {
  const {
    attributes: draggableAttributes,
    listeners,
    setNodeRef,
    transform,
  } = useDraggable({
    id: viewModel.id,
    attributes: { role: 'div' },
  });

  const ref = useRef<HTMLDivElement | null>(null);

  const style = {
    transform: CSS.Translate.toString(transform),
  };

  const name = viewModel.name.value;
  const updatedAt = viewModel.updatedAt;
  const publicReadable = viewModel.isPublicReadable(workspace.setting);
  const [modalTarget, setModalTarget] = useState<ModalTarget | null>(null);

  const [individualPermissions] = usePrimitiveValueListener(RTDBPath.ViewModel.individualPermissionsPath(viewModel.id));

  const handleEdit = () => setModalTarget('Edit');
  const handleDelete = () => setModalTarget('Delete');
  const handleModalClose = () => setModalTarget(null);
  const handleCopy = useViewModelCopy(viewModel);
  const handleMoveViewModel = () => setModalTarget('MoveViewModel');
  const handleMoveToOtherWorkspace = () => setModalTarget('MoveToOtherWorkspace');
  const handleConfigureIndividualPermissions = () => setModalTarget('ConfigureIndividualPermissions');

  const currentUserId = useCurrentUserId();

  // グループライセンスによる制限チェック
  const isRestricted = useIsViewModelRestrictedByGroupLicensePermission(viewModel.createdAt);

  useEffect(() => {
    if (shouldBeScrolled && ref.current) {
      ref.current.scrollIntoView();
    }
  }, [shouldBeScrolled]);

  return (
    <div ref={ref}>
      <div ref={setNodeRef} style={style} {...draggableAttributes}>
        <ItemContent
          viewModelId={viewModel.id}
          publicReadable={publicReadable}
          individualPermissionConfigured={!!(showConfigureIndividualPermissions && individualPermissions)}
          name={name}
          isLocked={viewModel.isLocked}
          updatedAt={updatedAt}
          showEditMenu={showEditMenu}
          noMenu={noMenu}
          onEdit={handleEdit}
          showConfigureIndividualPermissions={showConfigureIndividualPermissions}
          onConfigureIndividualPermissions={handleConfigureIndividualPermissions}
          onCopy={handleCopy}
          onDelete={handleDelete}
          onMoveViewModel={handleMoveViewModel}
          onMoveToOtherWorkspace={handleMoveToOtherWorkspace}
          dragListeners={isRestricted ? undefined : listeners}
          isRestricted={isRestricted}
        />
      </div>

      <ViewModelSettingModal
        isOpen={modalTarget === 'Edit'}
        onClose={handleModalClose}
        workspace={workspace}
        viewModel={viewModel}
      />

      <ConfigureIndividualPermissions
        isOpen={modalTarget === 'ConfigureIndividualPermissions'}
        onClose={handleModalClose}
        viewModel={viewModel}
      />

      <ViewModelDeleteConfirmModal viewModel={viewModel} isOpen={modalTarget === 'Delete'} onClose={handleModalClose} />

      <MoveViewModelModal isOpen={modalTarget === 'MoveViewModel'} onClose={handleModalClose} viewModel={viewModel} />

      {currentUserId && (
        <MoveToOtherWorkspaceModal
          isOpen={modalTarget === 'MoveToOtherWorkspace'}
          onClose={handleModalClose}
          viewModel={viewModel}
          userId={currentUserId}
          groupId={workspace.groupId}
          workspaceId={workspace.id}
        />
      )}
    </div>
  );
};
